import React, { useState, useEffect } from 'react';
import AdminService from '../services/adminService';
import '../css/admin/DeliveryPartnerDetails.css';
import {
  FaTruck,
  FaCheckCircle,
  FaTimes,
  FaTimesCircle,
  FaSpinner,
  FaMotorcycle,
  FaBicycle,
  FaCar,
  FaStar,
  FaChartBar,
  FaMobileAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBox,
  FaShoppingCart,
  FaArrowLeft,
  FaRoute,
  FaMapPin
} from 'react-icons/fa';

// Import Google Maps components directly
import { message } from 'antd';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  Polyline,
  InfoWindow
} from '@react-google-maps/api';

// Google Maps API key and libraries (static to prevent reloads)
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyBwRQy7Fwqg218NcVmpQyOFLGy9RWdJT1s';
const GOOGLE_MAPS_LIBRARIES = ['places'];

const DeliveryPartnerDetails = ({ providerId, providerData, onBack }) => {
  // Load Google Maps script once at component level
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOrders, setActiveOrders] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const format = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: format(firstDay), to: format(today) };
  });

  // Map state
  const [showMap, setShowMap] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderRoutes, setOrderRoutes] = useState({});
  const [mapCenter, setMapCenter] = useState({ lat: 13.5241, lng: 80.0057 });
  const [mapZoom, setMapZoom] = useState(12);
  const [providerLocation, setProviderLocation] = useState(null);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);

  // Use refs instead of state to avoid re-renders
  const mapInstanceRef = React.useRef(null);
  const polylineRef = React.useRef(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(12);

  useEffect(() => {
    if (providerData) {
      // Use provided data directly
      setProvider(providerData);
      setLoading(false);
      // Fetch orders for this provider
      fetchProviderOrders();
    } else if (providerId) {
      // Fetch from API if no data provided
      fetchProviderDetails();
    }
  }, [providerId, providerData]);

  // Reset current page when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const fetchProviderOrders = async () => {
    try {
      setOrdersLoading(true);
      setOrdersError(null);

      const targetUserId = provider?.provider_id || provider?.user_id || providerId;

      // Fetch all orders using the comprehensive method
      const ordersResponse = await AdminService.getAllDeliveryPartnerOrders(
        targetUserId,
        { date_from: dateRange.from, date_to: dateRange.to }
      );

      // Check if we have a valid response
      if (!ordersResponse) {
        console.error('No response received from getAllDeliveryPartnerOrders');
        throw new Error('No response received from API');
      }

      // Set the orders data with detailed logging
      let activeOrdersData = ordersResponse.active_orders || [];
      let orderHistoryData = ordersResponse.order_history || [];
      let allOrdersData = ordersResponse.combined_orders || [];

      // Apply frontend date filter
      if (dateRange.from && dateRange.to) {
        const fromDate = new Date(dateRange.from);
        fromDate.setHours(0, 0, 0, 0);
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59, 999);

        const filterOrders = (orders) => orders.filter(order => {
          if (!order.created_at) return true;
          const orderDate = new Date(order.created_at);
          return orderDate >= fromDate && orderDate <= toDate;
        });

        activeOrdersData = filterOrders(activeOrdersData);
        orderHistoryData = filterOrders(orderHistoryData);
        allOrdersData = filterOrders(allOrdersData);
      }

      setActiveOrders(activeOrdersData);
      setOrderHistory(orderHistoryData);
      setAllOrders(allOrdersData);
      setTotalOrderCount(allOrdersData.length);


    } catch (err) {
      console.error('Failed to load provider orders:', err);
      setOrdersError(`Failed to load orders: ${err.message}`);

      // Use mock data as fallback for testing
      const mockActiveOrders = [
        {
          order_id: 'ORD001',
          customer_name: 'John Doe',
          business_name: 'Pizza Palace',
          total_amount: 450,
          status: 'out_for_delivery',
          created_at: '2025-10-13T12:15:00Z',
          order_system: 'standard',
          order_type: 'active',
          customer_phone: '9876543210',
          delivery_charges: 25,
          estimated_delivery_time: '25-30 minutes'
        },
        {
          order_id: 'ORD003',
          customer_name: 'Mike Wilson',
          business_name: 'Coffee Corner',
          total_amount: 180,
          status: 'confirmed',
          created_at: '2025-10-13T13:45:00Z',
          order_system: 'grocery',
          order_type: 'active',
          customer_phone: '9123456789',
          delivery_charges: 15,
          estimated_delivery_time: '20-25 minutes'
        }
      ];

      const mockHistoryOrders = [
        {
          order_id: 'ORD002',
          customer_name: 'Jane Smith',
          business_name: 'Burger Hub',
          total_amount: 320,
          status: 'delivered',
          created_at: '2025-10-12T14:30:00Z',
          order_system: 'standard',
          order_type: 'completed',
          customer_phone: '9555666777',
          delivery_charges: 20,
          estimated_delivery_time: '30-35 minutes'
        },
        {
          order_id: 'ORD004',
          customer_name: 'Sarah Johnson',
          business_name: 'Fresh Mart',
          total_amount: 275,
          status: 'cancelled',
          created_at: '2025-10-11T16:20:00Z',
          order_system: 'grocery',
          order_type: 'completed',
          customer_phone: '9888777666',
          delivery_charges: 18,
          estimated_delivery_time: 'N/A'
        }
      ];

      const mockAllOrders = [...mockActiveOrders, ...mockHistoryOrders];

      setActiveOrders(mockActiveOrders);
      setOrderHistory(mockHistoryOrders);
      setAllOrders(mockAllOrders);
      setTotalOrderCount(mockAllOrders.length);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchProviderDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real data from admin API
      const response = await AdminService.getDeliveryProviderDetails(providerId);

      if (response && response.success && response.provider) {
        setProvider(response.provider);
      } else if (response && response.provider) {
        // Handle case where API returns provider data without success flag
        setProvider(response.provider);
      } else {
        throw new Error(response?.message || 'Failed to fetch provider details');
      }

      // Also fetch orders after getting provider details
      await fetchProviderOrders();
    } catch (err) {
      console.error('Failed to load provider details:', err);
      setError(err.message || 'Failed to load provider details');

      // Fallback to mock data if API fails
      const mockProvider = {
        provider_id: providerId,
        name: "Mike Wilson",
        phone: "+1234567890",
        email: "mike@example.com",
        delivery_phone: "+1234567890",
        vehicle_type: "motorcycle",
        registered_at: "2025-08-15T10:30:00Z",
        total_deliveries: 156,
        completed_deliveries: 142,
        status: "Available",
        current_location: "Sri City, Andhra Pradesh",
        rating: 4.8
      };

      setProvider(mockProvider);
      console.warn('Using fallback mock data due to API error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (provider) {
      fetchProviderOrders();
    }
  }, [dateRange.from, dateRange.to]);

  const getStatusColor = (status) => {
    const colors = {
      available: '#F55D00',
      busy: '#FDBF50',
      offline: '#2A2C41',
      '1': '#10b981',
      '0': '#6b7280'
    };
    return colors[String(status || '').toLowerCase()] || '#2A2C41';
  };

  const getVehicleIcon = (vehicleType) => {
    const icons = {
      motorcycle: <FaMotorcycle />,
      bicycle: <FaBicycle />,
      bike: <FaBicycle />,
      car: <FaCar />
    };
    return icons[String(vehicleType || '').toLowerCase()] || <FaCar />;
  };

  const formatDate = (dateString) => {
    return AdminService.formatDate(dateString);
  };

  // Helper function to validate and normalize coordinates
  const validateCoordinates = (lat, lng) => {
    // Default coordinates (Sri City area)
    const defaultLat = 13.5241;
    const defaultLng = 80.0057;

    // Check if coordinates are valid numbers
    const validLat = typeof lat === 'number' && !isNaN(lat) && lat >= -90 && lat <= 90 ? lat : defaultLat;
    const validLng = typeof lng === 'number' && !isNaN(lng) && lng >= -180 && lng <= 180 ? lng : defaultLng;

    return { lat: validLat, lng: validLng };
  };

  // Map helper functions
  const generateOrderRoute = async (order, providerLoc) => {
    try {


      // Enhanced coordinate detection with more field names
      const businessLat = parseFloat(
        order.business_lat || order.business_latitude ||
        order.pickup_lat || order.pickup_latitude ||
        order.restaurant_lat || order.restaurant_latitude
      );
      const businessLng = parseFloat(
        order.business_lng || order.business_longitude ||
        order.pickup_lng || order.pickup_longitude ||
        order.restaurant_lng || order.restaurant_longitude
      );
      const customerLat = parseFloat(
        order.consumer_lat || order.consumer_latitude ||
        order.customer_lat || order.customer_latitude ||
        order.delivery_lat || order.delivery_latitude
      );
      const customerLng = parseFloat(
        order.consumer_lon || order.consumer_lng || order.consumer_longitude ||
        order.customer_lon || order.customer_lng || order.customer_longitude ||
        order.delivery_lng || order.delivery_longitude
      );

      let businessLocation, customerLocation;

      if (!isNaN(businessLat) && !isNaN(businessLng) && !isNaN(customerLat) && !isNaN(customerLng)) {
        // Use real coordinates from database
        businessLocation = { lat: businessLat, lng: businessLng };
        customerLocation = { lat: customerLat, lng: customerLng };

      } else {
        // Enhanced fallback with realistic coordinates


        // Use provider location as base or default Sri City coordinates
        const baseLocation = providerLoc || { lat: 13.5241, lng: 80.0057 };

        // Generate realistic business location (closer to provider)
        const businessAngle = Math.random() * 2 * Math.PI;
        const businessRadius = 0.005 + Math.random() * 0.015; // 0.5-1.5 km
        businessLocation = {
          lat: baseLocation.lat + Math.cos(businessAngle) * businessRadius,
          lng: baseLocation.lng + Math.sin(businessAngle) * businessRadius
        };

        // Generate realistic customer location (further from business)
        const customerAngle = businessAngle + (Math.random() - 0.5) * Math.PI / 2; // Within 90 degrees of business direction
        const customerRadius = businessRadius + 0.01 + Math.random() * 0.02; // 1-3 km from business
        customerLocation = {
          lat: businessLocation.lat + Math.cos(customerAngle) * customerRadius,
          lng: businessLocation.lng + Math.sin(customerAngle) * customerRadius
        };

      }

      // Create route with waypoints from business to customer
      const route = [
        businessLocation, // Start (pickup)
        {
          lat: businessLocation.lat + (customerLocation.lat - businessLocation.lat) * 0.25,
          lng: businessLocation.lng + (customerLocation.lng - businessLocation.lng) * 0.25
        }, // Waypoint 1
        {
          lat: businessLocation.lat + (customerLocation.lat - businessLocation.lat) * 0.5,
          lng: businessLocation.lng + (customerLocation.lng - businessLocation.lng) * 0.5
        }, // Waypoint 2
        {
          lat: businessLocation.lat + (customerLocation.lat - businessLocation.lat) * 0.75,
          lng: businessLocation.lng + (customerLocation.lng - businessLocation.lng) * 0.75
        }, // Waypoint 3
        customerLocation // End (delivery)
      ];

      route.forEach((point, index) => {
      });

      const calculatedDistance = calculateRouteDistance(route);

      const routeData = {
        route,
        providerLocation: businessLocation, // Business location (RED marker)
        orderLocation: customerLocation, // Customer location (BLUE marker)
        distance: calculatedDistance,
        estimatedTime: Math.round(calculatedDistance * 3),
        directionsResult: null,
        locationHistory: null,
        isFallbackRoute: !(!isNaN(businessLat) && !isNaN(businessLng) && !isNaN(customerLat) && !isNaN(customerLng))
      };

      return routeData;
    } catch (error) {
      console.error('❌ ERROR generating route:', error);
      console.error('Error stack:', error.stack);

      // Ultimate fallback - ensure we always have valid coordinates
      const baseLocation = { lat: 13.5241, lng: 80.0057 };
      const businessLocation = {
        lat: baseLocation.lat + (Math.random() - 0.5) * 0.01,
        lng: baseLocation.lng + (Math.random() - 0.5) * 0.01
      };
      const customerLocation = {
        lat: businessLocation.lat + (Math.random() - 0.5) * 0.02,
        lng: businessLocation.lng + (Math.random() - 0.5) * 0.02
      };

      const route = [
        businessLocation,
        {
          lat: businessLocation.lat + (customerLocation.lat - businessLocation.lat) * 0.5,
          lng: businessLocation.lng + (customerLocation.lng - businessLocation.lng) * 0.5
        },
        customerLocation
      ];

      return {
        route,
        providerLocation: businessLocation,
        orderLocation: customerLocation,
        distance: calculateRouteDistance(route),
        estimatedTime: 5,
        directionsResult: null,
        locationHistory: null,
        isFallbackRoute: true
      };
    }
  };

  const calculateRouteDistance = (route) => {
    let totalDistance = 0;
    for (let i = 1; i < route.length; i++) {
      const R = 6371; // Earth's radius in km
      const dLat = (route[i].lat - route[i - 1].lat) * Math.PI / 180;
      const dLon = (route[i].lng - route[i - 1].lng) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(route[i - 1].lat * Math.PI / 180) * Math.cos(route[i].lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      totalDistance += R * c;
    }
    return totalDistance.toFixed(1);
  };

  const handleOrderTrack = async (order) => {
    setSelectedOrder(order);
    setIsCalculatingRoute(true);

    try {
      let routeData;

      // SIMPLIFIED APPROACH: Try GPS first, then guarantee fallback
      try {
        const locationData = await AdminService.getDeliveryLocationHistory(order.order_id);

        if (locationData && locationData.points && locationData.points.length > 1) {
          // Use GPS data
          const route = locationData.points.map(point => ({
            lat: parseFloat(point.latitude),
            lng: parseFloat(point.longitude)
          }));

          const businessLocation = locationData.startPoint ? {
            lat: parseFloat(locationData.startPoint.latitude),
            lng: parseFloat(locationData.startPoint.longitude)
          } : route[0];

          const customerLocation = locationData.endPoint ? {
            lat: parseFloat(locationData.endPoint.latitude),
            lng: parseFloat(locationData.endPoint.longitude)
          } : route[route.length - 1];

          routeData = {
            route,
            providerLocation: businessLocation,
            orderLocation: customerLocation,
            distance: calculateRouteDistance(route),
            estimatedTime: Math.round(calculateRouteDistance(route) * 3),
            isTrackedRoute: true,
            dataSource: 'deliverylocationhistory'
          };
        } else {
          throw new Error('No GPS data available');
        }
      } catch (gpsError) {
        message.error({
          content: <span style={{ fontSize: '16px', fontWeight: '500' }}>{gpsError.message || 'No GPS data available'}</span>,
          style: { marginTop: '60px' }
        });
        return;
      }


      // Store the route data
      setOrderRoutes(prev => {
        const newRoutes = {
          ...prev,
          [order.order_id]: routeData
        };
        return newRoutes;
      });

      // Center map
      if (routeData.route && routeData.route.length > 0) {
        const midPoint = routeData.route[Math.floor(routeData.route.length / 2)];
        setMapCenter(midPoint);
        setMapZoom(13);
      }
      setShowMap(true);

    } catch (error) {
      console.error('❌ Complete failure:', error);
      message.error({
        content: <span style={{ fontSize: '16px', fontWeight: '500' }}>Failed to load tracking data</span>,
        style: { marginTop: '60px' }
      });

    } finally {
      setIsCalculatingRoute(false);
    }
  };

  // Smooth GPS data to remove noise and outliers
  // Smooth GPS data to remove noise and outliers - VERY AGGRESSIVE filtering
  const smoothGPSData = (points) => {
    if (points.length <= 2) return points;



    // Step 1: Remove duplicate timestamps and very close points (< 100m)
    const deduplicated = [];
    const seenTimestamps = new Set();

    for (const point of points) {
      if (!seenTimestamps.has(point.timestamp)) {
        seenTimestamps.add(point.timestamp);

        if (deduplicated.length > 0) {
          const last = deduplicated[deduplicated.length - 1];
          const dist = calculatePointDistance(
            last.latitude, last.longitude,
            point.latitude, point.longitude
          );

          // Skip if less than 100 meters
          if (dist < 0.1) continue;
        }

        deduplicated.push(point);
      }
    }

    if (deduplicated.length <= 2) return deduplicated;

    // Step 2: Remove GPS jumps and zigzags
    const smoothed = [deduplicated[0]];

    for (let i = 1; i < deduplicated.length - 1; i++) {
      const prev = smoothed[smoothed.length - 1];
      const curr = deduplicated[i];

      const distFromPrev = calculatePointDistance(
        prev.latitude, prev.longitude,
        curr.latitude, curr.longitude
      );

      // Skip GPS jumps (> 1 km)
      if (distFromPrev > 1.0) {
        continue;
      }

      // Check for zigzags
      if (smoothed.length >= 2) {
        const prevPrev = smoothed[smoothed.length - 2];

        const bearing1 = Math.atan2(
          prev.latitude - prevPrev.latitude,
          prev.longitude - prevPrev.longitude
        );
        const bearing2 = Math.atan2(
          curr.latitude - prev.latitude,
          curr.longitude - prev.longitude
        );

        let bearingChange = Math.abs(bearing1 - bearing2) * (180 / Math.PI);
        if (bearingChange > 180) bearingChange = 360 - bearingChange;

        // Skip U-turns (> 140°) for distances < 400m
        if (bearingChange > 140 && distFromPrev < 0.4) {
          continue;
        }
      }

      smoothed.push(curr);
    }

    smoothed.push(deduplicated[deduplicated.length - 1]);

    return smoothed;
  };

  // Calculate distance between two GPS points in kilometers
  const calculatePointDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const initializeProviderLocation = async () => {
    try {
      // Try to get real location from provider data
      if (provider?.current_location) {
        const { latitude, longitude } = provider.current_location;
        if (latitude && longitude) {
          const location = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
          setProviderLocation(location);
          setMapCenter(location);
          return;
        }
      }

      // Try to get location from provider's last known position
      if (provider?.last_known_lat && provider?.last_known_lng) {
        const location = {
          lat: parseFloat(provider.last_known_lat),
          lng: parseFloat(provider.last_known_lng)
        };
        setProviderLocation(location);
        setMapCenter(location);
        return;
      }

      // Fallback: Use default location (Sri City area)
      const defaultLocation = {
        lat: 13.5241,
        lng: 80.0057
      };
      setProviderLocation(defaultLocation);
      setMapCenter(defaultLocation);
    } catch (error) {
      console.error('Error initializing provider location:', error);
      // Fallback to default
      const defaultLocation = { lat: 13.5241, lng: 80.0057 };
      setProviderLocation(defaultLocation);
      setMapCenter(defaultLocation);
    }
  };

  // Initialize provider location when provider data is loaded
  useEffect(() => {
    if (provider && !providerLocation) {
      initializeProviderLocation();
    }
  }, [provider]);

  // Map Component - Clean version with markers only
  const MapView = () => {
    const mapContainerStyle = {
      width: '100%',
      height: '500px',
      borderRadius: '12px'
    };

    const currentRoute = selectedOrder && orderRoutes[selectedOrder.order_id];



    if (!currentRoute) {
      return (
        <div className="partner-map-container">
          <div className="map-header">
            <h4>Delivery Tracking Map</h4>
            <button onClick={() => setShowMap(false)} className="close-map-btn">
              <FaTimes /> Close Map
            </button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading route data...
          </div>
        </div>
      );
    }
    if (currentRoute.error) {
      return (
        <div className="partner-map-container">
          <div className="map-header">
            <h4>Delivery Tracking Map</h4>
            <button onClick={() => setShowMap(false)} className="close-map-btn">
              <FaTimes /> Close Map
            </button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: '#EF4444', fontWeight: '500' }}>
            {currentRoute.error}
          </div>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="partner-map-container">
          <div className="map-header">
            <h4>Delivery Tracking Map</h4>
            <button onClick={() => setShowMap(false)} className="close-map-btn">
              <FaTimes /> Close Map
            </button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: '#dc3545' }}>
            Error loading Google Maps
          </div>
        </div>
      );
    }

    if (!isLoaded) {
      return (
        <div className="partner-map-container">
          <div className="map-header">
            <h4>Delivery Tracking Map</h4>
            <button onClick={() => setShowMap(false)} className="close-map-btn">
              <FaTimes /> Close Map
            </button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading Google Maps...
          </div>
        </div>
      );
    }

    const mapCenterPoint = currentRoute.route && currentRoute.route.length > 0
      ? currentRoute.route[Math.floor(currentRoute.route.length / 2)]
      : { lat: 13.5241, lng: 80.0057 };

    return (
      <div className="partner-map-container">
        <div className="map-header">
          <h4>Delivery Tracking Map</h4>
          <div className="map-controls">

            <button onClick={() => setShowMap(false)} className="close-map-btn">
              <FaTimes /> Close Map
            </button>
          </div>
        </div>

        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={13}
          center={mapCenterPoint}
          options={{
            zoomControl: true,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true
          }}
          onLoad={(map) => {
            mapInstanceRef.current = map;

            // Clean up old polyline if exists
            if (polylineRef.current) {
              polylineRef.current.setMap(null);
            }

            // Draw route line using native Google Maps API
            if (currentRoute && currentRoute.route && currentRoute.route.length > 1) {

              polylineRef.current = new window.google.maps.Polyline({
                path: currentRoute.route,
                geodesic: true,
                strokeColor: '#F55D00',
                strokeOpacity: 0.9,
                strokeWeight: 5,
                zIndex: 1
              });

              polylineRef.current.setMap(map);

              // Add START marker (RED) using native API
              if (currentRoute.providerLocation) {
                const startMarker = new window.google.maps.Marker({
                  position: currentRoute.providerLocation,
                  map: map,
                  icon: {
                    url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                  },
                  title: "START - Pickup Location",
                  zIndex: 1000
                });
              }

              // Add END marker (BLUE) using native API
              if (currentRoute.orderLocation) {
                const endMarker = new window.google.maps.Marker({
                  position: currentRoute.orderLocation,
                  map: map,
                  icon: {
                    url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                  },
                  title: "END - Delivery Location",
                  zIndex: 1000
                });
              }

              // Fit bounds to include route AND markers
              const bounds = new window.google.maps.LatLngBounds();

              // Add all route points
              currentRoute.route.forEach(point => bounds.extend(point));

              // IMPORTANT: Also add marker positions to bounds
              if (currentRoute.providerLocation) {
                bounds.extend(currentRoute.providerLocation);
              }
              if (currentRoute.orderLocation) {
                bounds.extend(currentRoute.orderLocation);
              }

              map.fitBounds(bounds);
            }
          }}
        >

          {/* Pickup Marker (Red) - START point */}
          {currentRoute?.providerLocation && (
            <>
              <Marker
                position={currentRoute.providerLocation}
                icon={{
                  url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                }}
                title="START - Pickup Location (Business)"
                zIndex={1000}
                onLoad={() => {
                }}
              />
            </>
          )}
          {!currentRoute?.providerLocation && console.log('⚠️ NO providerLocation - RED marker will not render')}

          {/* Delivery Marker (Blue) - END point */}
          {currentRoute?.orderLocation && (
            <>
              <Marker
                position={currentRoute.orderLocation}
                icon={{
                  url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                }}
                title="END - Delivery Location (Customer)"
                zIndex={1000}
                onLoad={() => {
                }}
              />
            </>
          )}
          {!currentRoute?.orderLocation && console.log('⚠️ NO orderLocation - BLUE marker will not render')}
        </GoogleMap>

        <div style={{
          marginTop: '15px',
          padding: '12px',
          background: currentRoute.dataSource === 'deliverylocationhistory' ? '#d1ecf1' :
            currentRoute.missingGPSData ? '#fff3cd' : 'white',
          borderRadius: '8px',
          border: currentRoute.dataSource === 'deliverylocationhistory' ? '1px solid #bee5eb' :
            currentRoute.missingGPSData ? '1px solid #ffeaa7' : '1px solid rgba(42, 44, 65, 0.08)',
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          fontSize: '13px'
        }}>
          <div style={{ fontWeight: '600', color: '#2A2C41' }}>
            {currentRoute.dataSource === 'deliverylocationhistory' ? '📍 GPS Route (deliverylocationhistory)' :
              currentRoute.missingGPSData ? '⚠️ Estimated Route (No GPS Data)' :
                '📍 Estimated Route'}:
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#dc3545' }}></span>
            <span>Start {currentRoute.providerLocation ? `(${currentRoute.providerLocation.lat.toFixed(4)}, ${currentRoute.providerLocation.lng.toFixed(4)})` : '(missing)'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#F55D00' }}></span>
            <span>Delivery Path</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#0d6efd' }}></span>
            <span>End {currentRoute.orderLocation ? `(${currentRoute.orderLocation.lat.toFixed(4)}, ${currentRoute.orderLocation.lng.toFixed(4)})` : '(missing)'}</span>
          </div>
          {currentRoute.dataSource === 'deliverylocationhistory' && (
            <div style={{ color: '#0c5460', fontSize: '12px', fontWeight: '500' }}>
              (GPS data from deliverylocationhistory table)
            </div>
          )}
          {currentRoute.isTrackedRoute && currentRoute.totalPoints && (
            <div style={{ color: '#666', fontSize: '12px' }}>
              ({currentRoute.totalPoints} GPS points)
            </div>
          )}
          {currentRoute.missingGPSData && (
            <div style={{ color: '#856404', fontSize: '12px', fontWeight: '500' }}>
              (Using estimated coordinates)
            </div>
          )}
          <div style={{ marginLeft: 'auto', color: '#666' }}>
            Distance: {currentRoute.distance} km | Est. Time: {currentRoute.estimatedTime} mins
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="delivery-partner-details-loading">
        <div className="loading-spinner"></div>
        <p>Loading delivery partner details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-partner-details-error">
        <div className="error-content">
          <FaTimesCircle className="error-icon" />
          <h3>Error Loading Details</h3>
          <p>{error}</p>
          <button className="back-btn" onClick={onBack}>
            <FaArrowLeft /> Back
          </button>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="delivery-partner-details-error">
        <div className="error-content">
          <FaTimesCircle className="error-icon" />
          <h3>Provider Not Found</h3>
          <p>The requested delivery partner could not be found.</p>
          <button className="back-btn" onClick={onBack}>
            <FaArrowLeft /> Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="delivery-partner-details">
      {/* Header */}
      <div className="details-header">
        <h2>Delivery Partner Details</h2>
        <button className="back-btn" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      </div>

      {/* Map View */}
      {showMap && <MapView />}

      {/* Details Grid */}
      <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', margin: '20px 0px' }}>
        {/* Personal Information */}
        <div style={{ background: 'white', padding: '16px', borderRadius: '10px', border: '1px solid rgba(42, 44, 65, 0.08)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2A2C41' }}>Personal Information</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>Name:</span>
              <span style={{ fontWeight: '600', color: '#2A2C41' }}>{provider.name}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>Delivery Phone:</span>
              <span style={{ fontWeight: '600', color: '#2A2C41' }}>{provider.delivery_phone}</span>
            </div>
          </div>
        </div>

        {/* Vehicle Information */}
        <div style={{ background: 'white', padding: '16px', borderRadius: '10px', border: '1px solid rgba(42, 44, 65, 0.08)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2A2C41' }}>Vehicle & Status</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', alignItems: 'center' }}>
              <span style={{ color: '#6c757d' }}>Vehicle Type:</span>
              <span style={{ fontWeight: '600', color: '#2A2C41', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {getVehicleIcon(provider.vehicle_type)} {provider.vehicle_type}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', alignItems: 'center' }}>
              <span style={{ color: '#6c757d' }}>Status:</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(provider.status)
                }}></span>
                <span style={{ fontWeight: '600', color: provider.status === 'Available' ? '#10b981' : '#F55D00' }}>{provider.status}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Provider Details */}
        <div style={{ background: 'white', padding: '16px', borderRadius: '10px', border: '1px solid rgba(42, 44, 65, 0.08)' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#2A2C41' }}>Provider Details</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>Provider ID:</span>
              <span style={{ fontWeight: '600', color: '#2A2C41' }}>{provider.provider_id}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>Registered On:</span>
              <span style={{ fontWeight: '600', color: '#2A2C41' }}>{formatDate(provider.registered_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Information */}
      <div className="detail-section orders-section">
        <div className="orders-header">
          <h4>Orders ({totalOrderCount} total deliveries)</h4>
          <div className="orders-tabs" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="date"
                title="Start Date"
                value={dateRange.from || ''}
                onChange={(e) => {
                  setDateRange(prev => ({
                    ...prev,
                    from: e.target.value || null
                  }));
                }}
                style={{ borderRadius: '6px', padding: '6px 10px', border: '1px solid #e2e8f0', color: '#64748b', outline: 'none', fontFamily: 'inherit', fontSize: '13px' }}
              />
              <span style={{ color: '#64748b', fontSize: '13px', fontWeight: '500' }}>to</span>
              <input
                type="date"
                title="End Date"
                value={dateRange.to || ''}
                onChange={(e) => {
                  setDateRange(prev => ({
                    ...prev,
                    to: e.target.value || null
                  }));
                }}
                style={{ borderRadius: '6px', padding: '6px 10px', border: '1px solid #e2e8f0', color: '#64748b', outline: 'none', fontFamily: 'inherit', fontSize: '13px' }}
              />
            </div>
            <button
              className="tab-btn active"
              onClick={() => setActiveTab('all')}
            >
              All Orders ({totalOrderCount})
            </button>
          </div>
        </div>

        {ordersLoading ? (
          <div className="orders-loading">
            <div className="loading-spinner"></div>
            <p>Loading orders...</p>
          </div>
        ) : ordersError ? (
          <div className="orders-error">
            <p>{ordersError}</p>
          </div>
        ) : (
          <div className="orders-content">
            {(() => {

              const renderOrdersList = (orders, emptyMessage) => {

                if (orders.length === 0) {
                  return (
                    <div className="no-orders">
                      <p>{emptyMessage}</p>
                    </div>
                  );
                }

                // Sort orders by created_at in descending order (latest first)
                const sortedOrders = [...orders].sort((a, b) => {
                  const dateA = new Date(a.created_at);
                  const dateB = new Date(b.created_at);
                  return dateB - dateA; // Latest first
                });


                // Pagination logic
                const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);
                const startIndex = (currentPage - 1) * ordersPerPage;
                const endIndex = startIndex + ordersPerPage;
                const currentOrders = sortedOrders.slice(startIndex, endIndex);

                return (
                  <>
                    <div className="orders-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {currentOrders.map((order) => (
                        <div key={order.order_id} style={{
                          background: 'white',
                          borderRadius: '10px',
                          padding: '14px',
                          border: selectedOrder?.order_id === order.order_id ? '2px solid #F55D00' : '1px solid rgba(42, 44, 65, 0.08)',
                          boxShadow: selectedOrder?.order_id === order.order_id ? '0 4px 12px rgba(245, 93, 0, 0.15)' : '0 2px 6px rgba(42, 44, 65, 0.04)',
                          transition: 'all 0.2s ease'
                        }}>
                          {/* Order Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid rgba(42, 44, 65, 0.06)' }}>
                            <div>
                              <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>ORDER ID</div>
                              <div style={{ fontSize: '14px', fontWeight: '700', color: '#2A2C41' }}>#{order.order_id}</div>
                            </div>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: order.status === 'DELIVERED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 93, 0, 0.1)',
                              color: order.status === 'DELIVERED' ? '#10b981' : '#F55D00'
                            }}>
                              {order.status || 'N/A'}
                            </span>
                          </div>

                          {/* Order Details */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                              <span style={{ color: '#6c757d' }}>Amount:</span>
                              <span style={{ fontWeight: '600', color: '#2A2C41' }}>₹{order.total_amount || 0}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                              <span style={{ color: '#6c757d' }}>Customer:</span>
                              <span style={{ fontWeight: '500', color: '#2A2C41', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>{order.customer_name || 'N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                              <span style={{ color: '#6c757d' }}>Business:</span>
                              <span style={{ fontWeight: '500', color: '#2A2C41', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>{order.business_name || 'N/A'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                              <span style={{ color: '#6c757d' }}>Date:</span>
                              <span style={{ fontWeight: '500', color: '#2A2C41' }}>{formatDate(order.created_at)}</span>
                            </div>
                          </div>

                          {/* Track Button */}
                          <button
                            onClick={() => handleOrderTrack(order)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              background: selectedOrder?.order_id === order.order_id ? '#2A2C41' : '#F55D00',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedOrder?.order_id !== order.order_id) {
                                e.currentTarget.style.background = '#2A2C41';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedOrder?.order_id !== order.order_id) {
                                e.currentTarget.style.background = '#F55D00';
                              }
                            }}
                          >
                            <FaRoute /> Track
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="pagination-container">
                        <div className="pagination-simple">
                          <button
                            className="pagination-arrow"
                            onClick={() => {
                              setCurrentPage(prev => Math.max(prev - 1, 1));
                            }}
                            disabled={currentPage === 1}
                          >
                            ‹
                          </button>

                          <span className="pagination-current">{currentPage}</span>
                          <span className="pagination-of">of {totalPages}</span>

                          <button
                            className="pagination-arrow"
                            onClick={() => {
                              setCurrentPage(prev => Math.min(prev + 1, totalPages));
                            }}
                            disabled={currentPage === totalPages}
                          >
                            ›
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              };

              return renderOrdersList(allOrders, 'No orders found for this delivery partner.');
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryPartnerDetails;